import {CanvasCodec} from '../codecs/canvas.codec';
import {canvasElementBackend} from '../codecs/canvas-element.backend';
import {offscreenCanvasBackend} from '../codecs/offscreen-canvas.backend';
import {createPreprocessWorker} from './create-preprocess-worker';
import type {ImagePreprocessor} from './image-preprocessor';
import {MainThreadImagePreprocessor} from './main-thread-image-preprocessor';
import {WorkerImagePreprocessor} from './worker-image-preprocessor';

export type RuntimeFeatures = {
  readonly hasWorker: boolean;
  readonly hasOffscreenCanvas: boolean;
  readonly hasConvertToBlob: boolean;
  readonly hasCreateImageBitmap: boolean;
};

export type PreprocessorRuntime = 'worker' | 'main-thread-offscreen' | 'main-thread-canvas';

export const detectPreprocessorRuntime = (features: RuntimeFeatures): PreprocessorRuntime => {
  const offscreen =
    features.hasOffscreenCanvas && features.hasConvertToBlob && features.hasCreateImageBitmap;
  if (offscreen && features.hasWorker) {
    return 'worker';
  }
  return offscreen ? 'main-thread-offscreen' : 'main-thread-canvas';
};

export const readRuntimeFeatures = (): RuntimeFeatures => {
  const hasOffscreenCanvas = typeof OffscreenCanvas === 'function';
  return {
    hasWorker: typeof Worker === 'function',
    hasOffscreenCanvas,
    hasConvertToBlob:
      hasOffscreenCanvas && typeof OffscreenCanvas.prototype.convertToBlob === 'function',
    hasCreateImageBitmap: typeof createImageBitmap === 'function',
  };
};

const createMainThreadOffscreen = (): ImagePreprocessor =>
  new MainThreadImagePreprocessor(new CanvasCodec(offscreenCanvasBackend));

const FACTORY_BY_RUNTIME: Readonly<Record<PreprocessorRuntime, () => ImagePreprocessor>> = {
  worker: () => new WorkerImagePreprocessor(createPreprocessWorker, createMainThreadOffscreen()),
  'main-thread-offscreen': createMainThreadOffscreen,
  'main-thread-canvas': () =>
    new MainThreadImagePreprocessor(new CanvasCodec(canvasElementBackend)),
};

let singleton: ImagePreprocessor | null = null;

/** Created on first use so importing the module touches no browser globals. */
export const getImagePreprocessor = (): ImagePreprocessor => {
  singleton ??= FACTORY_BY_RUNTIME[detectPreprocessorRuntime(readRuntimeFeatures())]();
  return singleton;
};
